import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Download,
  ExternalLink,
  Info,
  Loader2,
  OctagonAlert,
  Wrench,
} from 'lucide-react'
import { Pagination } from '@/components/Pagination'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button, buttonVariants } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Select } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/features/auth/useAuth'
import {
  CHECK_GROUP_LABELS,
  CHECKS,
  runAllChecks,
  type CheckDefinition,
  type CheckGroup,
  type CheckId,
  type Issue,
  type IssueFix,
  type Severity,
  type Source,
} from '@/features/inconsistencies/inconsistencies'
import { downloadIssuesCsv } from '@/features/inconsistencies/issues-csv'
import { saveLastCheck } from '@/features/inconsistencies/last-check'
import { subscribeToAllAttributeValues } from '@/features/materials/attribute-values-api'
import { subscribeToAllAttributes } from '@/features/materials/categories-api'
import { useCategories, useMaterials, useUnits } from '@/features/materials/useMaterials'
import { usePrices } from '@/features/prices/usePrices'
import { useRules } from '@/features/rules/useRules'
import { writeCategoryLinks } from '@/features/inconsistencies/category-links-api'
import { setCategoryOrder } from '@/features/materials/categories-api'
import { removeSectionGroups } from '@/features/rules/rules-api'
import { describeWriteError } from '@/features/users/permissions'
import type { MaterialAttributeValue, MaterialCategoryAttribute } from '@/lib/types'
import { usePagination } from '@/lib/use-pagination'
import { cn } from '@/lib/utils'

const GROUP_ORDER: CheckGroup[] = ['catalog', 'rules', 'translations']

const SEVERITY_STYLE: Record<Severity, { icon: typeof AlertTriangle; color: string; label: string }> = {
  error: { icon: OctagonAlert, color: 'text-destructive', label: 'ошибка' },
  warning: { icon: AlertTriangle, color: 'text-amber-500', label: 'предупреждение' },
  info: { icon: Info, color: 'text-sky-500', label: 'улучшение' },
}

/**
 * "Несоответствия": data that is saved but does not fit together, so the mobile app silently does
 * the wrong thing. Every check is a live list computed in the browser (`inconsistencies.ts`) from the
 * collections the other pages already read — no extra indexes, no writes. A row links to the page that
 * edits the affected item (`?edit=` / `?question=` …, see `useDeepLink`).
 */
export function InconsistenciesPage() {
  const { user } = useAuth()
  const materials = useMaterials(user?.uid)
  const categories = useCategories()
  const units = useUnits()
  const prices = usePrices(user?.uid)
  const rules = useRules()
  const attributes = useLiveCollection<MaterialCategoryAttribute>(subscribeToAllAttributes)
  const attributeValues = useLiveCollection<MaterialAttributeValue>(subscribeToAllAttributeValues)
  const [open, setOpen] = useState<CheckId | null>(null)

  const loading: Record<Source, boolean> = {
    materials: materials.isLoading,
    categories: categories.isLoading,
    attributes: attributes.isLoading,
    attributeValues: attributeValues.isLoading,
    units: units.isLoading,
    prices: prices.isLoading,
    rules: rules.isLoading,
  }

  const results = useMemo(
    () =>
      runAllChecks({
        materials: materials.materials,
        categories: categories.categories,
        attributes: attributes.items,
        attributeValues: attributeValues.items,
        units: units.units,
        prices: prices.prices,
        estateTypes: rules.estateTypes,
        sectionTypes: rules.sectionTypes,
        groups: rules.groups,
        hints: rules.hints,
        questions: rules.questions,
        options: rules.options,
        effects: rules.effects,
      }),
    [
      materials.materials,
      categories.categories,
      attributes.items,
      attributeValues.items,
      units.units,
      prices.prices,
      rules.estateTypes,
      rules.sectionTypes,
      rules.groups,
      rules.hints,
      rules.questions,
      rules.options,
      rules.effects,
    ],
  )

  const isCheckLoading = (check: CheckDefinition) => check.sources.some((source) => loading[source])
  const allLoaded = Object.values(loading).every((value) => !value)
  const totalOf = (severity: Severity) =>
    CHECKS.filter((check) => check.severity === severity).reduce(
      (sum, check) => sum + results[check.id].length,
      0,
    )
  const errorCount = totalOf('error')
  const warningCount = totalOf('warning')

  // The sidebar badge shows the count of the last complete run (see last-check.ts).
  useEffect(() => {
    if (allLoaded) saveLastCheck(errorCount, warningCount)
  }, [allLoaded, errorCount, warningCount])

  const errors = [
    materials.error,
    categories.error,
    units.error,
    prices.error,
    rules.error,
    attributes.error,
    attributeValues.error,
  ].filter((value): value is string => Boolean(value))

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Несоответствия</h1>
          <p className="text-muted-foreground text-sm">
            Данные, которые сохранены, но не сходятся между собой, — из-за них приложение молча
            считает или показывает не то. Списки обновляются сами, как только данные исправлены.
          </p>
        </div>
        {allLoaded ? (
          <div className="flex gap-2">
            <Badge variant={errorCount > 0 ? 'destructive' : 'secondary'}>ошибок: {errorCount}</Badge>
            <Badge variant="outline">предупреждений: {warningCount}</Badge>
          </div>
        ) : null}
      </div>

      {[...new Set(errors)].map((message) => (
        <Alert key={message} variant="destructive">
          <AlertTriangle />
          <AlertTitle>Часть данных не загружена — проверка может быть неполной</AlertTitle>
          <AlertDescription>{message}</AlertDescription>
        </Alert>
      ))}

      {GROUP_ORDER.map((group) => (
        <section key={group} className="flex flex-col gap-3">
          <h2 className="text-muted-foreground text-sm font-medium tracking-wide uppercase">
            {CHECK_GROUP_LABELS[group]}
          </h2>
          {CHECKS.filter((check) => check.group === group).map((check) => (
            <CheckSection
              key={check.id}
              check={check}
              issues={results[check.id]}
              isLoading={isCheckLoading(check)}
              isOpen={open === check.id}
              onToggle={() => setOpen((current) => (current === check.id ? null : check.id))}
            />
          ))}
        </section>
      ))}
    </div>
  )
}

/** Runs a quick fix (`Issue.fix`). Each kind is one minimal, targeted write. */
function applyFix(fix: IssueFix): Promise<void> {
  switch (fix.kind) {
    case 'unlink-section-groups':
      return removeSectionGroups(fix.sectionTypeId, fix.groupIds)
    case 'link-category':
      return writeCategoryLinks([fix.write])
    case 'renumber-categories':
      return setCategoryOrder(fix.updates)
  }
}

/**
 * Runs the fixes of many rows: category links go out together in batches, the rest one by one
 * (each touches its own document).
 */
async function applyFixes(fixes: IssueFix[]): Promise<void> {
  const links = fixes.flatMap((fix) => (fix.kind === 'link-category' ? [fix.write] : []))
  if (links.length > 0) await writeCategoryLinks(links)
  for (const fix of fixes) {
    if (fix.kind !== 'link-category') await applyFix(fix)
  }
}

/** A whole collection, live. */
function useLiveCollection<T>(
  subscribe: (onData: (items: T[]) => void, onError: (error: Error) => void) => () => void,
): { items: T[]; isLoading: boolean; error: string | null } {
  const [state, setState] = useState<{ items: T[]; isLoading: boolean; error: string | null }>({
    items: [],
    isLoading: true,
    error: null,
  })
  useEffect(
    () =>
      subscribe(
        (items) => setState({ items, isLoading: false, error: null }),
        (caught) => setState({ items: [], isLoading: false, error: caught.message }),
      ),
    [subscribe],
  )
  return state
}

/** One check: a clickable header with the count; the list opens below it. */
function CheckSection({
  check,
  issues,
  isLoading,
  isOpen,
  onToggle,
}: {
  check: CheckDefinition
  issues: Issue[]
  isLoading: boolean
  isOpen: boolean
  onToggle: () => void
}) {
  const count = issues.length
  const isEmpty = !isLoading && count === 0
  const style = SEVERITY_STYLE[check.severity]
  const Icon = isEmpty ? CheckCircle2 : style.icon
  return (
    <Card className="py-0">
      <CardContent className="px-0">
        <button
          type="button"
          onClick={onToggle}
          disabled={isLoading || isEmpty}
          aria-expanded={isOpen}
          className="hover:bg-accent/50 flex w-full items-start gap-3 rounded-xl px-5 py-4 text-left transition-colors disabled:cursor-default disabled:hover:bg-transparent"
        >
          <Icon
            className={cn(
              'mt-0.5 size-5 shrink-0',
              isLoading ? 'text-muted-foreground' : isEmpty ? 'text-emerald-500' : style.color,
            )}
            aria-label={isEmpty ? 'нет проблем' : style.label}
          />
          <span className="grid min-w-0 flex-1 gap-1">
            <span className="flex flex-wrap items-center gap-2 font-medium">
              {check.title}
              {isLoading ? (
                <Skeleton className="h-5 w-10" />
              ) : (
                <Badge
                  variant={
                    count === 0 ? 'secondary' : check.severity === 'error' ? 'destructive' : 'outline'
                  }
                >
                  {count === 0 ? 'нет' : count}
                </Badge>
              )}
            </span>
            <span className="text-muted-foreground text-xs">{check.description}</span>
          </span>
          {!isLoading && !isEmpty ? (
            <span className="text-primary flex shrink-0 items-center gap-1 text-sm">
              {isOpen ? 'Скрыть' : 'Показать'}
              <ChevronDown className={cn('size-4 transition-transform', isOpen && 'rotate-180')} />
            </span>
          ) : null}
        </button>
        {isOpen && !isLoading && count > 0 ? (
          <div className="border-t">
            <IssueTable check={check} issues={issues} />
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

/** The rows of one check: filter by problem type, CSV export, a link to each affected item. */
function IssueTable({ check, issues }: { check: CheckDefinition; issues: Issue[] }) {
  const [kind, setKind] = useState('')
  const kinds = useMemo(() => {
    const counts = new Map<string, number>()
    for (const issue of issues) counts.set(issue.kind, (counts.get(issue.kind) ?? 0) + 1)
    return [...counts.entries()].sort((left, right) => right[1] - left[1])
  }, [issues])
  // A filter value that disappeared (its last row was fixed) falls back to "all".
  const activeKind = kinds.some(([value]) => value === kind) ? kind : ''
  const visible = useMemo(
    () => (activeKind ? issues.filter((issue) => issue.kind === activeKind) : issues),
    [issues, activeKind],
  )
  const pager = usePagination(visible, 25)
  const [fixingId, setFixingId] = useState<string | null>(null)
  const [fixError, setFixError] = useState<string | null>(null)

  const fixable = visible.filter((issue) => issue.fix)
  const [isFixingAll, setFixingAll] = useState(false)
  const runAllFixes = async () => {
    setFixingAll(true)
    setFixError(null)
    try {
      await applyFixes(fixable.flatMap((issue) => (issue.fix ? [issue.fix] : [])))
    } catch (caught) {
      setFixError(describeWriteError(caught))
    } finally {
      setFixingAll(false)
    }
  }

  // The fixed row disappears by itself: the live listeners re-run the checks.
  const runFix = async (issue: Issue) => {
    if (!issue.fix) return
    setFixingId(issue.id)
    setFixError(null)
    try {
      await applyFix(issue.fix)
    } catch (caught) {
      setFixError(describeWriteError(caught))
    } finally {
      setFixingId(null)
    }
  }

  return (
    <>
      {fixError ? (
        <div className="px-4 pt-3">
          <Alert variant="destructive">
            <AlertTriangle />
            <AlertTitle>Не исправлено</AlertTitle>
            <AlertDescription>{fixError}</AlertDescription>
          </Alert>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
        {kinds.length > 1 ? (
          <Select
            value={activeKind}
            onChange={(event) => {
              setKind(event.target.value)
              pager.resetPage()
            }}
            className="w-auto min-w-56"
            aria-label="Вид проблемы"
          >
            <option value="">Все виды проблем ({issues.length})</option>
            {kinds.map(([value, count]) => (
              <option key={value} value={value}>
                {value} ({count})
              </option>
            ))}
          </Select>
        ) : (
          <span className="text-muted-foreground text-xs">{kinds[0]?.[0]}</span>
        )}
        <div className="flex flex-wrap gap-2">
          {fixable.length > 1 ? (
            <Button
              variant="outline"
              size="sm"
              disabled={fixingId !== null || isFixingAll}
              onClick={() => void runAllFixes()}
              title="Исправить автоматически все строки таблицы (с учётом фильтра)"
            >
              {isFixingAll ? <Loader2 className="animate-spin" /> : <Wrench />}
              Исправить все ({fixable.length})
            </Button>
          ) : null}
          <Button variant="outline" size="sm" onClick={() => downloadIssuesCsv(check, visible)}>
            <Download />
            CSV
          </Button>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{check.headers.subject}</TableHead>
            <TableHead>{check.headers.where}</TableHead>
            <TableHead>{check.headers.detail}</TableHead>
            <TableHead>Проблема</TableHead>
            <TableHead className="text-right">Открыть</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pager.pageItems.map((issue) => (
            <TableRow key={issue.id}>
              <TableCell className="max-w-64 align-top text-sm whitespace-normal">
                <div className="line-clamp-2 font-medium">{issue.subject}</div>
                {issue.subjectNote ? (
                  <div className="text-muted-foreground font-mono text-xs break-all">
                    {issue.subjectNote}
                  </div>
                ) : null}
              </TableCell>
              <TableCell className="align-top text-sm whitespace-normal">{issue.where || '—'}</TableCell>
              <TableCell className="max-w-56 align-top text-sm break-words whitespace-normal">
                {issue.detail || '—'}
              </TableCell>
              <TableCell className="align-top text-sm whitespace-normal">{issue.problem}</TableCell>
              <TableCell className="text-right align-top whitespace-nowrap">
                {issue.fix ? (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={fixingId !== null || isFixingAll}
                    onClick={() => void runFix(issue)}
                    title="Исправить автоматически"
                  >
                    {fixingId === issue.id ? <Loader2 className="animate-spin" /> : <Wrench />}
                    {issue.fix.label}
                  </Button>
                ) : null}
                {issue.link ? (
                  <Link
                    to={issue.link}
                    className={buttonVariants({ variant: 'ghost', size: 'sm' })}
                    aria-label={`Открыть: ${issue.subject}`}
                  >
                    <ExternalLink />
                    Открыть
                  </Link>
                ) : issue.fix ? null : (
                  <span className="text-muted-foreground text-xs">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Pagination
        page={pager.page}
        pageCount={pager.pageCount}
        pageSize={pager.pageSize}
        total={pager.total}
        firstShown={pager.firstShown}
        lastShown={pager.lastShown}
        onPage={pager.setPage}
        onPageSize={pager.setPageSize}
        itemsLabel="записей"
      />
    </>
  )
}
